import { startStreamingResponse } from '../api-fetch';
import { getSupportedMarkdowns } from './utils/get-supported-markdown-from-schema';
import { streamResponsePreview } from './utils/map-response-for-single-key-to-markdown';
import { runContextListAgainstFFs } from './utils/strip-context-list';
import { convoAIStatelessApiURL } from '../../prebuilt/utils';

/**
 * Generates some text.
 *
 * Intended for use in EditorPluginAI config items.
 * @see {@link [EditorPluginAI](../../editor-plugin-ai.tsx)}
 *
 * @example
 * ```ts
 * triggerPromptRequest({ initialContext }) {
 *   return generate({
 *     customPrompt: "Write a blog post about pets",
 *   });
 * }
 * ```
 *
 * @see https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Generate-API-Signature---Non-Streaming
 */
// Note -- the current plan is to collapse api interactions into a single
// mechanism as part of our move to "api v2"
// https://hello.atlassian.net/wiki/spaces/CA3/pages/2631558196/Spec+Platformized+API+spec+for+generative+AI+endpoint (updated api v2)

export const generate = input => {
  return function promptRequest(requestOptions) {
    const contextList = runContextListAgainstFFs(input.contextList);
    const streamingResponseGenerator = startStreamingResponse({
      endpoint: requestOptions.generativeAIApiUrl,
      payload: JSON.stringify({
        intentSchemaId: input.intentSchemaId,
        userInstruction: input.customPrompt,
        contextList,
        modelPlatform: 'OPENAI',
        outputLocale: input.userLocale,
        streaming: true,
        outputFormat: {
          outputFormatType: 'MARKDOWN',
          markdowns: getSupportedMarkdowns(input.editorSchema)
        },
        product: requestOptions.product,
        ...(input.backendModel === 'einstein' && {
          backendExperiment: 'EINSTEIN'
        })
      }),
      ...requestOptions
    });
    return streamResponsePreview({
      streamingResponseGenerator
    });
  };
};

/**
 * A generator function that will fetch the response from the Convo AI API
 */
export const generateConvoAI = input => {
  return function promptRequest(requestOptions) {
    const streamingResponseGenerator = startStreamingResponse({
      // This is the Convo AI 'stateless' endpoint (i.e. no session is maintained between requests with a channelID)
      endpoint: convoAIStatelessApiURL,
      payload: JSON.stringify({
        agent_input_prompt: input.customPrompt,
        recipient_agent_named_id: 'editor_agent',
        agent_input_context: {
          content: input.customPrompt,
          writing_intent: input.intentSchemaId,
          draft_full_content: input.fullDocument,
          draft_selected_content: input.latestPromptResponse ? input.latestPromptResponse : input.currentSelection,
          supported_markdown_nodes: getSupportedMarkdowns(input.editorSchema),
          input_mime_type: 'text/markdown',
          output_mime_type: 'text/markdown',
          locale: input.userLocale,
          product: requestOptions.product
        }
      }),
      ...requestOptions
    });
    return streamResponsePreview({
      streamingResponseGenerator,
      isConvoAI: true
    });
  };
};